import { Box } from '@mui/material'
import React, { useCallback, useReducer, useState } from 'react'
import CreateInsurancePolicyDialog from './createInsurancePolicyDialog';
import { CustomerService } from '../../../../service/customerService';
import { isLocalDev } from '../../../../utils/environment';
import useAbortController from '../../../../hooks/useAbortController';
import EnhancedTable from '../../../enhancedTable/EnhancedTable';
import { insurancePolicyListColumns } from './columns';
import ConfirmationDialog from '../../../ui/confirmationDialog';
import useDialogModal from '../../../../hooks/useDialogModal';
import validLookup from '../../../../utils/validLookup';
import { toast } from 'react-toastify';
import EditInsurancePolicyDialog from './editInsurancePolicyDialog';
import EditIcon from '@mui/icons-material/Edit';
import GroupsIcon from '@mui/icons-material/Groups';
import { useCan } from '../../../../hooks/useCan';
import { useEffect } from 'react';
import ManagePolicyFamilyLinksDialogComponent from './managePolicyFamilyLinksDialog';

function InsurancePolicy({ customer }) {
  const can = useCan();
  const [signal, abortRequest] = useAbortController();
  const customerId = customer?.customer?.id;
  // State to manage the Policy Selection Dialog
  const [openPolicyDialog, setOpenPolicyDialog] = useState(false);
  const [InsurancePolicyEditDialog, showInsurancePolicyEditForm] = useDialogModal(EditInsurancePolicyDialog);
  const [ManagePolicyFamilyLinksDialog, showManagePolicyFamilyLinksDialog] = useDialogModal(ManagePolicyFamilyLinksDialogComponent);
  const lastQueryRef = React.useRef(null);

  const [state, updateState] = useReducer((state, params) => {
    return { ...state, ...params };
  }, {
    data: [],
    totalRowCount: 0,
    pageCount: 0,
    filter: '',
    loading: false,
  });
  const [
    ConfirmationDialogComp,
    showConfirmationDialog,
    closeConfirmationDialog,
  ] = useDialogModal(ConfirmationDialog);

  useEffect(() => {
    return () => {
      abortRequest();
    };
  }, [abortRequest]);

  const fetchData = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
    lastQueryRef.current = { pageSize, pageIndex, filters, sortBy };
    const from = pageSize * pageIndex;
    updateState({ loading: true });
    CustomerService.getInsurancePolicies(from, pageSize, filters, sortBy, customerId, signal).then(result => {
      const { code, data } = result;
      if (code === 0) {
        updateState({
          data: data.data,
          totalRowCount: data.count,
          pageCount: Math.ceil(data.count / pageSize),
        });
      }
      updateState({ loading: false });
    }).catch(error => {
      if (isLocalDev) {
        console.error(error)
      }
      updateState({ loading: false });
    });
  }, [customerId, signal]);

  const refreshPolicies = useCallback(() => {
    if (lastQueryRef.current) {
      fetchData(lastQueryRef.current);
    }
  }, [fetchData]);

  const deleteRecordHandler = (rowids) => {
    showConfirmationDialog(async (action) => {
      if (action === validLookup.confirmationDialogAction.ok) {
        const deleteRecordResult = await CustomerService.deleteInsurancePolicy(customer.customer.id, rowids.map(x => parseInt(x, 10)));
        const { code, message } = deleteRecordResult;

        if (code === 0) {
          toast.success(message);
          refreshPolicies();
        } else {
          toast.success("Failed to delete record(s)");
        }
      }
      closeConfirmationDialog();
    }, {
      title: 'Delete record',
      message: `Please confirm if you would like to delete ${rowids.length} record(s)?`,
    });
  }

  const handleRowEdit = (rowData) => {
    showInsurancePolicyEditForm(null, { recordToEdit: rowData });
  }

  const handleManageFamilyLinks = (rowData) => {
    showManagePolicyFamilyLinksDialog(null, { policy: rowData });
  };

  const isPolicyHolder = (rowData) => {
    if (!rowData) return false;
    // For legacy policies without holderCustomerId, allow current linked customer to manage.
    if (!rowData.holderCustomerId) return true;
    return Number(rowData.holderCustomerId) === Number(customerId);
  };

  const recordEditHandler = (record) => {
    // find the employee by row id
    const existingValidSupplier = state.data;
    const idx = existingValidSupplier.findIndex(e => e.id === record.id);
    const updatesList = existingValidSupplier.map((row, index) => {
      if (row.id === record.id) {
        return {
          ...existingValidSupplier[idx], ...record,
        }
      }
      return row
    });

    // update list
    updateState({
      data: updatesList
    });
    refreshPolicies();
  }

  const recordCreatedHandler = (newRecord, selectedProvider) => {
    if (!newRecord) return;

    // Fetch fresh rows so nested provider association is always hydrated.
    if (lastQueryRef.current) {
      refreshPolicies();
      return;
    }

    // Fallback only when table hasn't issued first query yet.
    updateState({
      data: [{ ...newRecord, provider: newRecord.provider || selectedProvider || null }, ...state.data],
      totalRowCount: state.totalRowCount + 1,
    });
  }

  return (
    <Box display="flex" flexDirection="column">
      <EnhancedTable
        columns={insurancePolicyListColumns}
        addRecordHandler={can.action('insurance', 'add-insurance') ? () => setOpenPolicyDialog(true) : undefined}
        deleteRecordHandler={can.action('insurance', 'delete-insurance') ? deleteRecordHandler : null}
        data={state.data}
        totalRowCount={state.totalRowCount}
        pageCount={state.pageCount}
        fetchData={fetchData}
        loading={state.loading}
        selectionMode="multi"
        actions={[
          // Edit Policy - requires insurance.edit-insurance permission
          ...(can.action('insurance', 'edit-insurance') ? [{
            icon: <EditIcon />,
            onClick: handleRowEdit
          }] : []),
          ...(can.action('insurance', 'edit-insurance') ? [{
            icon: <GroupsIcon />,
            onClick: handleManageFamilyLinks,
            tooltip: (rowData) => isPolicyHolder(rowData)
              ? "Manage family links"
              : "Only policy holder can manage family links",
            disabled: (rowData) => !customer.customer.familyGroupId || !isPolicyHolder(rowData),
          }] : [])
        ]}
      />
      <CreateInsurancePolicyDialog customer={customer} open={openPolicyDialog} onClose={() => setOpenPolicyDialog(false)} onCreated={recordCreatedHandler} />
      <InsurancePolicyEditDialog customer={customer} onRecordEdit={recordEditHandler} />
      <ManagePolicyFamilyLinksDialog customer={customer} />

      <ConfirmationDialogComp
        label={{
          positive: "Confirm",
          negative: "Cancel",
        }} />
    </Box>
  )
}

export default InsurancePolicy